import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Order } from '../types';
import { orderHistoryService, calculateItemTotal } from '../services/orderHistoryService';
import { 
  ShoppingBag, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Trash2,
  Search,
  Filter,
  Calendar,
  Receipt
} from 'lucide-react';

interface OrderHistoryProps {
  currency?: string;
}

export const OrderHistory: React.FC<OrderHistoryProps> = ({ currency = '€' }) => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<Order[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'preparing' | 'completed'>('all');
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'week' | 'month'>('all');
  const [showFilters, setShowFilters] = useState(false);

  // Charger les commandes au montage et écouter les mises à jour
  useEffect(() => {
    const loadOrders = () => {
      const storedOrders = orderHistoryService.getOrders();
      setOrders(storedOrders);
    };

    loadOrders();

    // Écouter les mises à jour de l'historique
    const handleHistoryUpdate = () => {
      loadOrders();
    };

    window.addEventListener('orderHistoryUpdated', handleHistoryUpdate);
    return () => window.removeEventListener('orderHistoryUpdated', handleHistoryUpdate);
  }, []);

  // Filtrer les commandes
  useEffect(() => {
    let filtered = [...orders];

    // Filtre par recherche
    if (searchTerm) {
      filtered = filtered.filter(order => 
        order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.items.some(item => 
          item.menuItem.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Filtre par statut
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    // Filtre par date
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.createdAt);
        
        switch (dateFilter) {
          case 'today':
            return orderDate >= today;
          case 'week':
            const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
            return orderDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
            return orderDate >= monthAgo;
          default:
            return true;
        }
      });
    }

    setFilteredOrders(filtered);
  }, [orders, searchTerm, statusFilter, dateFilter]);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });
  };

  const getStatusIcon = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return <Clock size={16} className="text-orange-500" />;
      case 'preparing':
        return <AlertCircle size={16} className="text-blue-500" />;
      case 'completed':
        return <CheckCircle size={16} className="text-green-500" />;
      default:
        return <Clock size={16} className="text-gray-500" />;
    }
  };

  const getStatusText = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return t(' ');
      case 'preparing':
        return t(' ');
      case 'completed':
        return t(' ');
      default:
        return t(' ');
    }
  }; 

  const clearHistory = () => {
    if (window.confirm(t('Êtes-vous sûr de vouloir vider l\'historique ?'))) {
      orderHistoryService.clearHistory();
    }
  };

  if (orders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-gray-500">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <ShoppingBag size={36} className="text-gray-400" />
        </div>
        <p className="text-lg font-medium text-gray-700 mb-2">{t('Aucun historique de commande')}</p>
        <p className="text-sm text-center text-gray-500">
          {t('Vos commandes passées apparaîtront ici après votre première commande')}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* En-tête avec recherche et filtres */}
      <div className="p-4 border-b border-gray-100 bg-white sticky top-0 z-10">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <Receipt size={20} className="text-orange-500" />
            {t('Historique des commandes')}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-lg transition-colors ${
                showFilters ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              aria-label={t('Filtres')}
            >
              <Filter size={16} />
            </button>
            <button
              onClick={clearHistory}
              className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
              aria-label={t('Vider l\'historique')}
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>

        {/* Barre de recherche */}
        <div className="relative mb-3">
          <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={t('Rechercher par numéro ou produit...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          />
        </div>

        {/* Filtres */}
        {showFilters && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('Statut')}
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="w-full p-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="all">{t('Tous les statuts')}</option>
                <option value="pending">{t('En attente')}</option>
                <option value="preparing">{t('En préparation')}</option>
                <option value="completed">{t('Terminée')}</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('Période')}
              </label>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value as any)}
                className="w-full p-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="all">{t('Toutes les dates')}</option>
                <option value="today">{t('Aujourd\'hui')}</option>
                <option value="week">{t('Cette semaine')}</option>
                <option value="month">{t('Ce mois')}</option>
              </select>
            </div>
          </div>
        )}

        {/* Compteur de résultats */}
        <div className="mt-3 text-xs text-gray-500">
          {filteredOrders.length} {t('commande(s) trouvée(s)')} sur {orders.length}
        </div>
      </div>

      {/* Liste des commandes */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {filteredOrders.map((order) => (
          <div
            key={order.id}
            className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 hover:shadow-md transition-shadow"
          >
            {/* En-tête de la commande */}
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-bold text-gray-800">
                    #{order.id.slice(-8).toUpperCase()}
                  </span>
                  <div className="flex items-center gap-1">
                    {getStatusIcon(order.status)}
                    <span className="text-xs font-medium">
                      {getStatusText(order.status)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Calendar size={12} />
                  <span>{formatDate(order.createdAt)}</span>
                  {order.orderType && (
                    <>
                      <span>•</span>
                      <span>{order.orderType === 'takeaway' ? t('À emporter') : t('Sur place')}</span>
                    </>
                  )}
                  {order.tableNumber && (
                    <>
                      <span>•</span>
                      <span>{t('Table')} {order.tableNumber}</span>
                    </>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-orange-600">
                  {order.total.toFixed(2)} {currency}
                </div>
                {order.paymentMethod && (
                  <div className="text-xs text-gray-500">
                    {order.paymentMethod === 'cash' ? t('Espèces') : t('Carte')}
                  </div>
                )}
              </div>
            </div>

            {/* Items de la commande */}
            <div className="space-y-2">
              {order.items.map((item, index) => (
                <div key={`${item.uniqueId || item.menuItem.id}-${index}`} className="text-sm">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <span className="font-medium text-gray-800">
                        {item.quantity}× {item.menuItem.name}
                      </span>
                      {Object.entries(item.customizations).map(([specName, options]) => (
                        <div key={specName} className="text-xs text-gray-500 ml-4 mt-1">
                          {options.map((opt, i) => (
                            <span key={opt.id}>
                              {opt.name}
                              {opt.price > 0 && ` (+${opt.price.toFixed(2)} ${currency})`}
                              {i < options.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </div>
                      ))}
                    </div>
                    <span className="text-sm font-medium text-gray-700 ml-2">
                      {(item.menuItem.price * item.quantity + 
                        Object.values(item.customizations).reduce((sum, options) => 
                          sum + options.reduce((optSum, opt) => optSum + (opt.price || 0), 0), 0
                        ) * item.quantity
                      ).toFixed(2)} {currency}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Total */}
            <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">
                {t('Total')} ({order.items.length} {t('article(s)')})
              </span>
              <span className="text-lg font-bold text-gray-900">
                {order.total.toFixed(2)} {currency}
              </span>
            </div>
          </div>
        ))}

        {filteredOrders.length === 0 && orders.length > 0 && (
          <div className="text-center py-8 text-gray-500">
            <Search size={32} className="mx-auto mb-3 text-gray-400" />
            <p className="font-medium">{t('Aucun résultat trouvé')}</p>
            <p className="text-sm">{t('Essayez de modifier vos critères de recherche')}</p>
          </div>
        )}
      </div>
    </div>
  );
};